<script setup lang="ts">
import type { CoursesResponse } from '~~/shared/types/course'

const props = defineProps<{
  title: string
  description?: string | null
  slug: string
  to: string
  icon?: string
  coverImage?: string | null
  level?: string | null
}>()

// Daten werden im Browser geladen, damit neue Termine ohne Rebuild sichtbar sind.
// `getCachedData: () => undefined` erzwingt den Fetch auch bei direktem URL-Aufruf
// (sonst würde der Hydration-Payload den Default-Wert als "bereits geladen" behandeln).
const { data: courseData, status } = useAsyncData(
  `coursetype-${props.slug}`,
  () => $fetch<CoursesResponse>('/api/courses', { query: { slug: props.slug } }),
  {
    server: false,
    lazy: true,
    default: () => ({ courses: [] }),
    getCachedData: () => undefined
  }
)

const isLoading = computed(() => status.value === 'pending' || status.value === 'idle')

const nextCourse = computed(() => courseData.value?.courses?.[0])

function formatDate(dateStr: string): string {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('de-CH', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const badge = computed(() => {
  if (isLoading.value) return null
  const course = nextCourse.value
  if (!course) {
    return { label: 'Auf Anfrage', color: 'neutral' as const }
  }
  if (course.is_confirmed) {
    return { label: 'Bestätigt', color: 'success' as const }
  }
  return null
  //return { label: 'Provisorisch', color: 'warning' as const }
})
</script>

<template>
  <NuxtLink
    :to="to"
    class="group block h-full"
  >
    <UCard class="h-full flex flex-col transition-shadow group-hover:ring-2 group-hover:ring-primary">
      <template #header>
        <div class="flex items-start justify-between gap-2">
          <div class="flex items-center gap-3 min-w-0">
            <UIcon
              v-if="icon"
              :name="icon"
              class="text-primary text-xl shrink-0"
            />
            <h3 class="font-semibold text-base leading-tight">
              {{ title }}
            </h3>
          </div>
          <UBadge
            v-if="level"
            color="success"
            variant="subtle"
            class="shrink-0"
          >
            {{ level }}
          </UBadge>
        </div>
      </template>

      <div
        v-if="description || coverImage"
        class="flex items-stretch gap-3"
      >
        <p
          v-if="description"
          class="text-sm text-muted line-clamp-3 w-1/2"
        >
          {{ description }}
        </p>
        <div
          v-if="coverImage"
          class="relative w-1/2 min-h-24 rounded overflow-hidden bg-white"
        >
          <img
            :src="coverImage"
            alt=""
            class="absolute inset-0 w-full h-full object-cover"
          >
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-2 text-sm text-muted">
            <UIcon
              name="i-lucide-calendar"
              class="shrink-0"
            />
            <span v-if="isLoading">Termine werden geladen …</span>
            <span v-else-if="nextCourse">
              Nächster Kurs: {{ formatDate(nextCourse.startdate) }}
            </span>
            <span v-else>Kein Termin ausgeschrieben</span>
          </div>
          <UBadge
            v-if="badge"
            :color="badge.color"
            variant="subtle"
            class="shrink-0"
          >
            {{ badge.label }}
          </UBadge>
        </div>
      </template>
    </UCard>
  </NuxtLink>
</template>
